import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as moment from "moment-timezone";

import { RotaCalendar } from "@teamrota/rota-design";

import Icon, { ICON_NAMES as Icons } from "~/src/components/icon";

import ShiftStream from "../..";
import Pagination from "../../components/pagination";
import { useClients } from "../../graphql/use-clients";
import { useRoles } from "../../graphql/use-roles";
import { useAccounts } from "../../graphql/use-accounts";
import {
  CalendarWrapper,
  CheckboxLabel,
  FilterGrid,
  FilterGridRow,
  FilterLinks,
  FilterSelect,
  FilterSlot,
  FilterTextInput,
  FooterControls,
  FooterLabel,
  OrderBySelect,
  PaginationSlot,
  RefreshButton,
  SplitFilterSlot,
} from "../styles";

import { EmailOrder, useEmails } from "./graphql/use-emails";
import Table from "./Table";
import {
  FilterType,
  getDefaultFilterState,
  getSavedFilterState,
  saveFilterState,
} from "./filters";

export const COL_WIDTHS = ["8vw", "13vw", "15vw", "16vw", "30vw", "6vw"];

type ShiftStreamEmailAccount = {
  id: number;
  provider: string;
  url: string;
};

export const Emails = () => {
  const { accountId } = useParams<{
    accountId?: string;
  }>();

  const { accounts } = useAccounts() as { accounts: ShiftStreamEmailAccount[] };
  const { clients } = useClients();

  const [filters, setFilters] = useState<FilterType>(getSavedFilterState());
  const [currentPage, setCurrentPage] = useState(1);
  const [orderBy, setOrderBy] = useState<EmailOrder>("ID");

  useEffect(() => saveFilterState(filters), [filters]);

  // add debounce to avoid many queries during typing
  const [senderFilterText, setSenderFilterText] = useState<string>("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setFilters({ ...filters, sender: senderFilterText });
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [senderFilterText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.inbox,
    filters.sender,
    filters.state,
    filters.client,
    filters.role,
    filters.includeExpired,
    filters.includeUnmapped,
    orderBy,
  ]);

  useEffect(() => {
    setFilters({ ...filters, role: "all" });
  }, [filters.client]);

  useEffect(() => {
    if (accountId) {
      const account = accounts.find((a) => a.id == parseInt(accountId));
      if (account) {
        setFilters({ ...filters, inbox: account.url });
      }
    }
  }, [accountId, accounts]);

  const { roles } = useRoles({
    clientIds: filters.client !== "all" ? [filters.client] : null,
  });

  const { emails, totalPages, loading, refetch } = useEmails(
    {
      sender: filters.sender !== "" ? filters.sender : null,
      inbox: filters.inbox !== "all" ? filters.inbox : null,
      sourceAccountId: filters.client !== "all" ? filters.client : null,
      roleId: filters.role !== "all" ? filters.role : null,
      venueId: filters.venue !== "all" ? filters.venue : null,
      state: filters.state !== "all" ? filters.state : null,
      startDate: filters.startDate,
      endDate: filters.endDate,
      includeExpired: filters.includeExpired,
      includeUnmapped: filters.includeUnmapped,
      orderBy,
    },
    currentPage,
  );

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, loading]);

  const handleDateRangeChange = ({
    startDate,
    endDate,
  }: {
    startDate?: Date;
    endDate?: Date;
  }) => {
    let startMoment = startDate ? moment(startDate).startOf("day") : null;
    let endMoment = endDate ? moment(endDate).startOf("day") : null;

    if (startMoment && endMoment) {
      if (startMoment.isAfter(endMoment)) {
        setFilters({ ...filters, endDate: endMoment, startDate: startMoment });
      } else {
        setFilters({ ...filters, startDate: startMoment, endDate: endMoment });
      }
    }
  };

  const handleClearFilters = () => {
    setFilters(getDefaultFilterState());
    setCurrentPage(1);
    setSenderFilterText("");
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    const scrollBody = document.querySelector(".emails-table-scroll-body");
    if (scrollBody instanceof HTMLElement) {
      scrollBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleOrderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrderBy(e.target.value as EmailOrder);
    setCurrentPage(1);

    const scrollBody = document.querySelector(".emails-table-scroll-body");
    if (scrollBody instanceof HTMLElement) {
      scrollBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <ShiftStream>
      <FilterLinks>
        Quick Links{" "}
        <a
          href="#"
          onClick={() =>
            handleDateRangeChange({
              startDate: new Date(),
              endDate: moment().endOf("day").toDate(),
            })
          }
        >
          Today Only
        </a>{" "}
        <a
          href="#"
          onClick={() =>
            setFilters({ ...getDefaultFilterState(), state: "ANY_PARSE" })
          }
        >
          Any Parses
        </a>{" "}
        <a
          href="#"
          onClick={() =>
            setFilters({ ...getDefaultFilterState(), state: "PARTIAL" })
          }
        >
          Partial Parses
        </a>
        <a
          href="#"
          onClick={() =>
            setFilters({ ...getDefaultFilterState(), state: "COULD_NOT_PARSE" })
          }
        >
          Could Not Parse
        </a>
        <CheckboxLabel htmlFor="emails-include-expired">
          <input
            id="emails-include-expired"
            type="checkbox"
            checked={filters.includeExpired}
            onChange={(e) =>
              setFilters({ ...filters, includeExpired: e.target.checked })
            }
          />
          Include Expired
        </CheckboxLabel>
        <CheckboxLabel htmlFor="emails-include-unmapped">
          <input
            id="emails-include-unmapped"
            type="checkbox"
            checked={filters.includeUnmapped}
            onChange={(e) =>
              setFilters({ ...filters, includeUnmapped: e.target.checked })
            }
          />
          Include Clients Not Enabled
        </CheckboxLabel>
        <a
          href="#"
          onClick={handleClearFilters}
          style={{ float: "right", padding: "12px 0" }}
        >
          Clear Filters
        </a>
      </FilterLinks>

      <FilterGrid>
        <FilterGridRow colWidths={COL_WIDTHS}>
          <FilterSlot>
            <FilterSelect
              id="emails-inbox-filter"
              value={filters.inbox ?? "all"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  inbox: e.target.value === "all" ? "all" : e.target.value,
                })
              }
            >
              <option value="all">All Inboxes</option>
              {accounts
                .filter((a: { provider?: string }) => a.provider === "email")
                .map((account: { id: number; url: string }) => (
                  <option key={account.id} value={account.url}>
                    {account.url}
                  </option>
                ))}
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <FilterTextInput
              id="emails-to-filter"
              type="text"
              value={senderFilterText}
              placeholder="To filter"
              onChange={(e) => setSenderFilterText(e.target.value)}
            />
          </FilterSlot>

          <FilterSlot style={{ justifyContent: "center" }}>
            <CalendarWrapper>
              <RotaCalendar
                key={`calendar-${filters.startDate?.toISOString() ?? "null"}-${filters.endDate?.toISOString() ?? "null"}`}
                isSelectsRange
                startDate={(filters.startDate?.toDate() ?? null) as any}
                endDate={filters.endDate?.toDate() ?? null}
                onChange={handleDateRangeChange}
              />
            </CalendarWrapper>
          </FilterSlot>

          <FilterSlot>
            <FilterSelect
              id="emails-state-filter"
              value={filters.state}
              onChange={(e) =>
                setFilters({ ...filters, state: e.target.value })
              }
            >
              <option value="all">Any Status</option>
              <option value="ANY_PARSE">Any Parse</option>
              <option value="PARSED">Parsed</option>
              <option value="PARTIAL">Partial Parse</option>
              <option value="COULD_NOT_PARSE">Could Not Parse</option>
              <option value="ERROR">Error</option>
              <option value="SKIPPED">Skipped</option>
              <option value="PENDING">Pending</option>
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <SplitFilterSlot>
              <FilterSelect
                id="emails-client-filter"
                value={filters.client}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    client:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Clients</option>
                {clients.map((client: { id: number; accountName: string }) => (
                  <option key={client.id} value={client.id}>
                    {client.accountName}
                  </option>
                ))}
              </FilterSelect>

              <FilterSelect
                id="emails-role-filter"
                value={filters.role}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    role:
                      e.target.value === "all" ? "all" : Number(e.target.value),
                  })
                }
              >
                <option value="all">All Roles</option>
                {roles.map((role: { id: number; name: string }) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </FilterSelect>
            </SplitFilterSlot>
          </FilterSlot>

          <RefreshButton
            type="button"
            aria-label="Refresh"
            title="Refresh"
            disabled={loading}
            onClick={handleRefresh}
          >
            <Icon
              name={Icons.REFRESH}
              size={18}
              color={loading ? Icon.colors.GREY_DARK : Icon.colors.MAIN_ORANGE}
              onClick={handleRefresh}
            />
          </RefreshButton>
        </FilterGridRow>
      </FilterGrid>
      <Table emails={emails} loading={loading} />
      <FooterControls>
        <FooterLabel>Order By</FooterLabel>
        <OrderBySelect
          id="stream-order-by"
          value={orderBy}
          onChange={handleOrderChange}
        >
          <option value="ID">ID#</option>
          <option value="SENDER">Sender</option>
          <option value="INBOX">Inbox</option>
          <option value="RECEIVED_AT">Received At</option>
          <option value="CLIENT">Client</option>
          <option value="VENUE">Venue</option>
          <option value="ROLE">Role</option>
          <option value="START">Start</option>
        </OrderBySelect>

        <PaginationSlot>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        </PaginationSlot>
      </FooterControls>
    </ShiftStream>
  );
};
